import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../notifications/useNotification';
import axios from 'axios';
import './ReportProblemModal.css';

/**
 * Pages available in the select dropdown.
 * `value` is sent to the backend; `i18nKey` is the translation key for display.
 */
const PAGE_OPTIONS = [
    { value: 'Home',            i18nKey: 'Home' },
    { value: 'Calendar',        i18nKey: 'Calendar' },
    { value: 'Clients',         i18nKey: 'Clients' },
    { value: 'Client Details',  i18nKey: 'client-details' },
    { value: 'Foods & Recipes', i18nKey: 'foods-sidebar-text' },
    { value: 'Profile',         i18nKey: 'profile' },
    { value: 'Other',           i18nKey: 'Other' },
];

/**
 * ReportProblemModal — lets a dietist report a problem on a specific page.
 *
 * @param {{ onClose: () => void }} props
 */
const ReportProblemModal = ({ onClose }) => {
    const { t } = useTranslation();
    const notification = useNotification();

    const [page, setPage] = useState('');
    const [description, setDescription] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const newErrors = {};
        if (!page) newErrors.page = t('rpm-page-required');
        if (!description.trim()) newErrors.description = t('rpm-description-required');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSend = async () => {
        if (!validate()) return;
        try {
            setLoading(true);
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/report-problem`,
                { page, description },
                { withCredentials: true },
            );
            notification.showSuccess(t('rpm-sent-success'));
            onClose();
        } catch {
            notification.showError(t('rpm-sent-error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rpm-overlay" onClick={onClose}>
            <div className="rpm-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="rpm-header">
                    <h2 className="rpm-title">{t('rpm-title')}</h2>
                    <button className="rpm-close-btn" onClick={onClose} aria-label="Close">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Body */}
                <div className="rpm-body">

                    {/* Page select */}
                    <div className="rpm-field">
                        <label className="rpm-label">
                            {t('rpm-page-label')}
                            <span className="rpm-required">*</span>
                        </label>
                        <select
                            className="rpm-select"
                            value={page}
                            onChange={e => { setPage(e.target.value); setErrors(prev => ({ ...prev, page: '' })); }}
                        >
                            <option value="" disabled>{t('rpm-select-page')}</option>
                            {PAGE_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{t(opt.i18nKey)}</option>
                            ))}
                        </select>
                        {errors.page && <span className="rpm-error">{errors.page}</span>}
                    </div>

                    {/* Description */}
                    <div className="rpm-field">
                        <label className="rpm-label">
                            {t('rpm-description-label')}
                            <span className="rpm-required">*</span>
                        </label>
                        <textarea
                            className="rpm-textarea"
                            value={description}
                            onChange={e => { setDescription(e.target.value); setErrors(prev => ({ ...prev, description: '' })); }}
                            placeholder={t('rpm-description-placeholder')}
                            maxLength={2000}
                        />
                        {errors.description && <span className="rpm-error">{errors.description}</span>}
                    </div>
                </div>

                {/* Footer */}
                <div className="rpm-footer">
                    <button className="rpm-btn rpm-btn-cancel" onClick={onClose}>
                        {t('Cancel')}
                    </button>
                    <button className="rpm-btn rpm-btn-send" onClick={handleSend} disabled={loading}>
                        {loading ? t('sending') : t('rpm-send')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ReportProblemModal;
